import { __awaiter } from '../../../../../_virtual/_tslib.js';
import { useState, useMemo } from 'react';
import { useInternalDynamicContext } from '../../../context/DynamicContext/useInternalDynamicContext.js';
import { unlinkOAuth, verifyOAuth } from '../../../data/api.js';
import { useUpdateUserProfileByJWTCallback } from '../useUpdateUserProfileByJWTCallback/useUpdateUserProfileByJWTCallback.js';
import '@dynamic-labs/sdk-api';
import { logger } from '../../../shared/logger.js';
import '@dynamic-labs/iconic';
import '@dynamic-labs/wallet-connector-core';
import 'react/jsx-runtime';
import '../../../context/ViewContext/ViewContext.js';
import '@dynamic-labs/wallet-book';
import '@dynamic-labs/utils';
import '../../constants/colors.js';
import '../../../shared/utils/classes/storage/localStorage.js';
import 'ethers';
import '../../../shared/consts/index.js';
import '@dynamic-labs/multi-wallet';
import { getAuthToken } from '../../functions/getAuthToken/getAuthToken.js';
import { getEnabledOAuthProviders } from '../../functions/getEnabledOAuthProviders/index.js';
import { isSocialKycEnabled } from '../../functions/isSocialKycEnabled/isSocialKycEnabled.js';

const socialProviders = [
    'discord',
    'facebook',
    'github',
    'google',
    'instagram',
    'twitch',
    'twitter',
];
const useSocialAccounts = () => {
    var _a;
    const { environmentId, user, projectSettings, onboardingOnlyJwt } = useInternalDynamicContext();
    const { updateUserProfileByJWT } = useUpdateUserProfileByJWTCallback();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState();
    const userJwt = (_a = getAuthToken()) !== null && _a !== void 0 ? _a : onboardingOnlyJwt;
    const oauthProviders = getEnabledOAuthProviders(projectSettings === null || projectSettings === void 0 ? void 0 : projectSettings.providers);
    const verifiedOAuthCredentialsMap = useMemo(() => {
        var _a, _b;
        const verifiedOAuthCredential = (_b = (_a = user === null || user === void 0 ? void 0 : user.verifiedCredentials) === null || _a === void 0 ? void 0 : _a.filter((credential) => credential.format === 'oauth')) !== null && _b !== void 0 ? _b : [];
        return Object.fromEntries(new Map(verifiedOAuthCredential.map((credential) => {
            var _a;
            return [
                (_a = credential.oauthProvider) !== null && _a !== void 0 ? _a : credential.id,
                credential,
            ];
        })));
    }, [user === null || user === void 0 ? void 0 : user.verifiedCredentials]);
    const handleError = (message) => {
        logger.error(message);
        setIsProcessing(false);
        setError(message);
    };
    const checkValidProvider = (provider) => {
        if (!provider) {
            handleError('Provider is required');
            return false;
        }
        if (!socialProviders.includes(provider)) {
            handleError(`Invalid social provider. Valid providers are: ${socialProviders.join(', ')}`);
            return false;
        }
        return true;
    };
    const linkSocialAccount = (provider) => __awaiter(void 0, void 0, void 0, function* () {
        setError(undefined);
        setIsProcessing(true);
        if (!isSocialKycEnabled(projectSettings)) {
            handleError('Social linking is not enabled on the dashboard');
            return;
        }
        if (!checkValidProvider(provider)) {
            return;
        }
        const verifiedCredential = verifiedOAuthCredentialsMap[provider];
        if (verifiedCredential) {
            handleError(`Account already linked for provider: ${provider}`);
            return;
        }
        const oauthProvider = oauthProviders.find((p) => p.provider === provider);
        if (!oauthProvider) {
            handleError(`Provider is not enabled on the dashboard: ${provider}`);
            return;
        }
        const handleAuthWindowMessage = (event) => __awaiter(void 0, void 0, void 0, function* () {
            var _b, _c, _d;
            if (((_b = event.data) === null || _b === void 0 ? void 0 : _b.type) === 'authorization_response') {
                logger.debug('Message received', { data: event.data });
            }
            if (((_c = event.data) === null || _c === void 0 ? void 0 : _c.type) !== 'authorization_response' ||
                ((_d = event.data) === null || _d === void 0 ? void 0 : _d.provider) !== provider) {
                return;
            }
            const { code } = event.data;
            const jwt = yield verifyOAuth(environmentId, provider, {
                code,
            }, String(userJwt));
            if (!jwt) {
                handleError(`Failed to link social account: ${provider}`);
                return;
            }
            yield updateUserProfileByJWT(jwt);
            setIsProcessing(false);
        });
        window.addEventListener('message', handleAuthWindowMessage);
        const authWindow = window.open(oauthProvider.authorizationUrl, '_blank', 'width=500,height=600');
        const authWindowInterval = setInterval(() => {
            if (authWindow === null || authWindow === void 0 ? void 0 : authWindow.closed) {
                window.removeEventListener('message', handleAuthWindowMessage);
                clearInterval(authWindowInterval);
                setIsProcessing(false);
            }
        }, 1000);
    });
    const unlinkSocialAccount = (provider) => __awaiter(void 0, void 0, void 0, function* () {
        var _e;
        setError(undefined);
        setIsProcessing(true);
        if (!checkValidProvider(provider)) {
            return;
        }
        const verifiedCredential = verifiedOAuthCredentialsMap[provider];
        if (!verifiedCredential) {
            handleError(`No account linked for provider: ${provider}`);
            return;
        }
        try {
            const jwt = yield unlinkOAuth({
                environmentId,
                verifiedCrentialId: (_e = verifiedCredential.id) !== null && _e !== void 0 ? _e : '',
            }, String(userJwt));
            if (!jwt) {
                handleError(`Failed to unlink social account: ${provider} | ${verifiedCredential.id}`);
                return;
            }
            yield updateUserProfileByJWT(jwt);
            setIsProcessing(false);
        }
        catch (err) {
            handleError(`Failed to unlink social account: ${provider} | ${verifiedCredential.id}`);
        }
    });
    const isLinked = (provider) => Boolean(verifiedOAuthCredentialsMap[provider]);
    const getLinkedAccountInformation = (provider) => {
        const verifiedCredential = verifiedOAuthCredentialsMap[provider];
        if (!verifiedCredential) {
            return;
        }
        const { id, oauthAccountId, oauthAccountPhotos, oauthDisplayName, oauthEmails, oauthProvider, oauthUsername, publicIdentifier, } = verifiedCredential;
        return {
            accountId: oauthAccountId,
            avatar: oauthAccountPhotos === null || oauthAccountPhotos === void 0 ? void 0 : oauthAccountPhotos[0],
            displayName: oauthDisplayName,
            email: oauthEmails === null || oauthEmails === void 0 ? void 0 : oauthEmails[0],
            id,
            provider: oauthProvider,
            publicIdentifier,
            username: oauthUsername,
        };
    };
    return {
        error,
        getLinkedAccountInformation,
        isLinked,
        isProcessing,
        linkSocialAccount,
        unlinkSocialAccount,
    };
};

export { useSocialAccounts };
